/*
 * Manage Install Form
 * Edit Install items
 */

import * as React from 'react';

import { Button, Col, FormLabel, FormGroup, Row } from 'react-bootstrap';
import { AbstractControl, FieldConfig, FormGenerator, GroupProps, Validators } from 'react-reactive-form';
import {
    bulkUpdateInstalls,
    deleteInstall,
    saveInstall,
    setEnableInstallBatchMode,
    setInstallFormValues,
    updateInstall,
    updateInstallFormValues
} from '../../actions/manageInventoryActions';
import {
    Ibuilding,
    Ifacility,
    Ifloor,
    IinstallBasePopulated,
    Ijob,
    Ilocation,
    Iproduct,
    IproductInfo,
    ItableFiltersReducer,
    Iuser
} from '../../models';
import { initialInstallBasePopulated, initialProduct } from '../../reducers/initialState';

import { TFunction } from 'i18next';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { RouteComponentProps } from 'react-router';
import { capitalEquipmentCategories, constants, productPlaceholderImages } from '../../constants/constants';
import { IinstallFormValues } from '../../modelsForms';
import { FormUtil } from '../common/FormUtil';
import { msalFetch } from '../auth/Auth-Utils';
import API from '../../constants/apiEndpoints';
import { WithTranslation } from 'react-i18next';

const locationGroupFields = ['buildingID', 'floorID', 'locationID'];

interface Iprops extends RouteComponentProps<any> {
    updateInstall: typeof updateInstall;
    saveInstall: typeof saveInstall;
    toggleModal: () => void;
    selectedItem: IinstallBasePopulated;
    loading: boolean;
    colorButton: string;
    t: TFunction;
    tableFilters: ItableFiltersReducer;
    selectedProduct: Iproduct;
    mainCategoryID: string;
    facility: Ifacility;
    deleteInstall: typeof deleteInstall;
    saveAnyLocation: (
        name: string,
        facilityID: string,
        installBaseLocations?: {
            buildingID?: string;
            floorID?: string;
            locationID?: string;
        },
        skipModalToggle?: boolean
    ) => { id: string } | any;
    user: Iuser;
    isJobReadOnly: boolean;
    showCancel?: boolean;
    productInfo: IproductInfo;
    enableBulkInstallBaseMode?: boolean;
    bulkUpdateInstalls: typeof bulkUpdateInstalls;
    selection?: string[];
    setEnableInstallBatchMode: typeof setEnableInstallBatchMode;
    setInstallFormValues: typeof setInstallFormValues;
    updateInstallFormValues: typeof updateInstallFormValues;
    formValues: IinstallFormValues;
    selectedJob: Ijob;
    isAPIQueueEmpty: boolean;
}

interface Istate {
    fieldConfig: FieldConfig;
    isGeneratingSerialNumber: boolean;
}

class ManageInstallForm extends React.Component<Iprops & WithTranslation, Istate> {
    private formGroup: FormGroup | any;
    private subscription: any;
    private updateFormValueDebounced: any;
    static defaultProps = {
        selectedItem: initialInstallBasePopulated,
        selectedProduct: initialProduct
    };

    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.updateFormValueDebounced = debounce(this.props.updateInstallFormValues, constants.formDebounceTime);
        this.state = {
            fieldConfig: { controls: {} },
            isGeneratingSerialNumber: false
        };
    }

    componentDidMount() {
        const { selectedProduct, selectedItem, selection, enableBulkInstallBaseMode, i18n } = this.props;

        // we should have a product if adding new, an id if editing, or in bulk mode.
        if (
            (!selectedProduct || (selectedProduct && selectedProduct.id.length === 0)) &&
            (!selectedItem || (selectedItem && !selectedItem.id.length)) &&
            !this.props.bulkUpdateInstalls
        ) {
            console.error('[componentDidMount]: missing product and install');
        }

        // Only disable bulk mode in very specific scenarios where it's clearly inappropriate
        // If we got here via startBulkEditInstall, we should respect that intent
        const isNewInstallMode = !!(selectedProduct && selectedProduct.id && (!selection || selection.length === 0));

        // Only disable bulk mode if we're clearly in "new install" mode with no selection
        const shouldDisableBulkMode = enableBulkInstallBaseMode && isNewInstallMode;

        if (shouldDisableBulkMode) {
            this.props.setEnableInstallBatchMode(false);
        }

        this.props.setInstallFormValues(this.itemToFormValues());
        this.setState({
            fieldConfig: this.buildFieldConfig(this.itemToFormValues())
        });

        i18n.on('languageChanged', () => {
            this.setState({ fieldConfig: this.buildFieldConfig(this.itemToFormValues()) });
        });
    }

    componentDidUpdate(prevProps: Iprops) {
        if (
            JSON.stringify(prevProps.selectedItem) !== JSON.stringify(this.props.selectedItem) &&
            this.props.selectedItem.id.length
        ) {
            this.setState({
                fieldConfig: this.buildFieldConfig(this.itemToFormValues())
            });
            this.props.setInstallFormValues(this.itemToFormValues());
        }
        // since the date-time controls manage their own state, we cannot patch them
        if (prevProps.formValues.prodDate !== this.props.formValues.prodDate) {
            this.setState({ fieldConfig: this.buildFieldConfig() });
        }

        if (
            JSON.stringify(prevProps.formValues.floorID) !== JSON.stringify(this.props.formValues.floorID) ||
            JSON.stringify(prevProps.formValues.locationID) !== JSON.stringify(this.props.formValues.locationID) ||
            JSON.stringify(prevProps.formValues.roomID) !== JSON.stringify(this.props.formValues.roomID) ||
            JSON.stringify(prevProps.formValues.buildingID) !== JSON.stringify(this.props.formValues.buildingID)
        ) {
            // this.setState({ fieldConfig: this.buildFieldConfig() });
            this.updateFormGroup();
        }
        if (JSON.stringify(this.props.facility) !== JSON.stringify(prevProps.facility)) {
            this.setState({ fieldConfig: this.buildFieldConfig() });
        }
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    // MMG-4200: Building/Floor/Location are all-or-nothing once the user edits
    // any of them (create or edit); untouched legacy location data is left alone
    isLocationGroupRequired = (values: { [key: string]: any }) => {
        const item = this.props.selectedItem as { [key: string]: any };
        const id = (key: string) => (values[key] && values[key].value) || '';
        const edited = locationGroupFields.some(key => id(key) !== (item[key] || ''));
        const started = locationGroupFields.some(key => id(key) !== '');
        return edited && started;
    };

    locationGroupValidator = (control: AbstractControl) => {
        if (control.value || !this.formGroup) {
            return null;
        }
        const values: { [key: string]: any } = {};
        locationGroupFields.forEach(key => {
            const field = this.formGroup.get(key);
            values[key] = field ? field.value : undefined;
        });
        return this.isLocationGroupRequired(values) ? { required: true } : null;
    };

    updateFormGroup = (defaultValues: IinstallFormValues = this.prepValuesFromRedux()) => {
        const requireLocation = this.isLocationGroupRequired(defaultValues);
        locationGroupFields.forEach(key => {
            const control = this.formGroup.get(key);
            if (control && control.meta) {
                control.meta.required = requireLocation;
            }
        });
        forEach(defaultValues, (value, key) => {
            FormUtil.patchControl(this.formGroup, key, value);
        });
        // patch the options
        FormUtil.patchControl(this.formGroup, 'buildingID', defaultValues.buildingID, defaultValues.buildingOptions);
        FormUtil.patchControl(this.formGroup, 'floorID', defaultValues.floorID, defaultValues.floorOptions);
        FormUtil.patchControl(this.formGroup, 'locationID', defaultValues.locationID, defaultValues.locationOptions);
        FormUtil.patchControl(this.formGroup, 'roomID', defaultValues.roomID, defaultValues.roomOptions);
    };

    /*
     * itemToFormValues - take the selectedItem and convert it to formValues
     */
    itemToFormValues = (): IinstallFormValues => {
        let { buildingID, floorID, locationID, roomID, system } = this.props.selectedItem;

        let selectedSystemsOption;
        if (system) {
            selectedSystemsOption = {
                value: system,
                label: this.props.t('systemsOptionLabel', {
                    count: system
                })
            };
        }
        // when creating new, default to the selected locations in the table filters
        if (!this.props.selectedItem.id.length) {
            const { building, floor, location, rooms } = this.props.tableFilters;
            buildingID = building ? building.value : buildingID;
            floorID = floor ? floor.value : floorID;
            locationID = location ? location.value : locationID;
            // Only pre-fill the room when exactly one is selected in the filter.
            roomID = rooms && rooms.length === 1 ? rooms[0]?.value ?? roomID : roomID;
        }

        const cleanedItem = omit(this.props.selectedItem, [
            'product',
            'workOrders',
            'latestMeasurementPointListResult',
            'facility'
        ]);

        return {
            ...cleanedItem,
            sapMaterialNumber: this.props.selectedItem.product.sapMaterialNumber,
            system: selectedSystemsOption ?? { value: 0, label: '' },
            ...this.getAllFilteredLocations(buildingID, floorID, locationID, roomID)
        };
    };

    /*
     * formValuesToItem - convert the formValues to the shape of the selectedItem
     */
    formValuesToItem = (): any => {
        const formValues = FormUtil.getValues(this.formGroup.value);
        const cleanedItem = omit(this.props.selectedItem, [
            // if the user cleared any values out of the form on update, we don't want to just send back in the original install values
            ...Object.keys(this.state.fieldConfig.controls).filter(fieldName => formValues[fieldName] === undefined),
            'product',
            'workOrders',
            'fullLocation',
            'latestMeasurementPointListResult'
        ]);

        // verify the facilities match
        if (
            this.props.selectedItem.facilityID &&
            this.props.selectedItem.facilityID.length &&
            this.props.selectedItem.facilityID !== this.props.facility.id
        ) {
            console.error('[formValuesToItem]:', this.props.selectedItem, this.props.facility.id);
            toastr.error(
                this.props.t('toastMessage:error'),
                this.props.t('toastMessage:errorMatchingFacilities'),
                constants.toastrError
            );
            throw new Error('facilities do not match');
        }

        let cleanedInstall = {
            ...cleanedItem,
            ...formValues,
            materialID: this.props.selectedItem.materialID || this.props.selectedProduct.id,
            facilityID: this.props.facility.id,
            quantity: parseInt(`${formValues.quantity}`, 10)
        };

        // Make sure there are no empty strings for IDs
        cleanedInstall.latestAGSMeasurementPointListResultID =
            cleanedInstall.latestAGSMeasurementPointListResultID === ''
                ? ''
                : cleanedInstall.latestAGSMeasurementPointListResultID ?? '';
        cleanedInstall.latestMeasurementPointListResultID =
            cleanedInstall.latestMeasurementPointListResultID === ''
                ? ''
                : cleanedInstall.latestMeasurementPointListResultID ?? '';
        cleanedInstall.latestVerificationMeasurementPointListResultID =
            cleanedInstall.latestVerificationMeasurementPointListResultID === ''
                ? ''
                : cleanedInstall.latestVerificationMeasurementPointListResultID ?? '';
        cleanedInstall.latestAuditMeasurementPointListResultID =
            cleanedInstall.latestAuditMeasurementPointListResultID === ''
                ? ''
                : cleanedInstall.latestAuditMeasurementPointListResultID ?? '';
        cleanedInstall.latestCommissioningMeasurementPointListResultID =
            cleanedInstall.latestCommissioningMeasurementPointListResultID === ''
                ? ''
                : cleanedInstall.latestCommissioningMeasurementPointListResultID ?? '';
        // System is a non-nullable int in the API; 0 means no system
        cleanedInstall.system = cleanedInstall.system ?? 0;

        return cleanedInstall;
    };

    openServiceConnect = (serialNumber: string | undefined) => {
        if (serialNumber) {
            window.open(
                `https://serviceconnect.compressedairbusiness.com/MachineDetail.aspx?SerialNumber=${serialNumber}`,
                '_blank'
            );
        } else {
            toastr.error(
                this.props.t('toastMessage:error'),
                'No serial number provided. Required to open Service Connect.',
                constants.toastrError
            );
        }
    };
    /*
     * (reusable)
     * subscribe to the formGroup changes
     */
    subscribeToChanges = () => {
        for (const key in this.formGroup.controls) {
            if (this.formGroup.controls.hasOwnProperty(key)) {
                this.subscription = this.formGroup.get(key).valueChanges.subscribe((value: any) => {
                    this.onValueChanges(value, key);
                });
            }
        }
    };
    /*
     * (reusable)
     * set the table filters to redux on each value change
     */
    onValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'prodDate':
                this.handleNewManufactureDate(value);
                break;
            default:
                if (moment.isMoment(value)) {
                    value = value.format(constants.momentSQLFormat);
                }
                this.updateFormValueDebounced({ [key]: value });
                break;
        }
    };

    /*
     * prepValuesFromRedux
     * help filter and build the selected locations, and options
     */
    prepValuesFromRedux = () => {
        const formValues = FormUtil.getValues(this.props.formValues) as {
            [key: string]: any;
        };

        return {
            ...this.props.formValues,
            ...this.getAllFilteredLocations(
                formValues.buildingID,
                formValues.floorID,
                formValues.locationID,
                formValues.roomID
            )
        };
    };

    /*
  * buildFieldConfig
  accepts defaultValues and returns a formConfig object
  */
    buildFieldConfig = (defaultValues: IinstallFormValues = this.prepValuesFromRedux()) => {
        const isCapitalEquipmentType = Object.values(capitalEquipmentCategories).includes(this.props.mainCategoryID);
        let quantityValidators: any = [];
        const disabled = this.canEditInstalls() === false;
        const shouldRequireQuantity =
            this.props.selectedItem.id.length || this.props.enableBulkInstallBaseMode === true ? false : true;

        if (shouldRequireQuantity) {
            quantityValidators = [Validators.min(1), Validators.max(1000), FormUtil.validators.requiredWithTrim];
        }
        const shouldRequireLocation = this.isLocationGroupRequired(defaultValues);

        const fieldConfigControls = {
            $field_0: {
                isStatic: false, // ensures a key is added
                render: () => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FormUtil.TextLabelStatic
                            meta={{
                                label: this.props.t('productInfo'),
                                colWidth: 10,
                                value: this.props.selectedProduct.name
                            }}
                        />
                        <img
                            width="50"
                            height="50"
                            style={{ marginRight: 15, background: 'white' }}
                            src={this.props.selectedProduct.imagePath || ''}
                            onError={(e: any) => {
                                e.target.onerror = null;
                                const placeholder = productPlaceholderImages.find(
                                    img => img.categoryId.toLocaleLowerCase() === this.props.mainCategoryID
                                );
                                if (placeholder) {
                                    e.target.src = require(`../../images/products/${placeholder.src}`);
                                } else {
                                    e.target.src = require('../../images/Azure.png');
                                    console.error(
                                        '[buildFieldConfig -> field_0 -> onError]: missing image for product',
                                        this.props.mainCategoryID
                                    );
                                }
                            }}
                            alt="icon"
                        />
                    </div>
                )
            },
            nickname: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'nickname',
                    colWidth: 12,
                    type: 'input',
                    name: 'nickname',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(40)
                },
                formState: { value: defaultValues.nickname, disabled }
            },
            serialNumber: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'serialNumber',
                    colWidth: 6,
                    type: 'input',
                    name: 'serial-number',
                    required: isCapitalEquipmentType,
                    t: this.props.t
                },
                options: {
                    validators: isCapitalEquipmentType
                        ? [Validators.maxLength(20), Validators.required, Validators.pattern(/^[\w-/]+$/)]
                        : [Validators.maxLength(20), Validators.pattern(/^[\w-/]+$/)]
                },
                formState: { value: defaultValues.serialNumber, disabled }
            },
            customActionButtons: {
                render: () => (
                    <Col xs={6} className="custom-action-buttons">
                        <Button
                            variant="primary"
                            onClick={() => this.generateSerialNumber()}
                            disabled={disabled}
                            aria-label="Generate Serial Number"
                        >
                            Generate S/N
                        </Button>
                        {defaultValues.serialNumber && (
                            <Button
                                variant="primary"
                                onClick={() => {
                                    if (typeof defaultValues.serialNumber === 'string') {
                                        this.openServiceConnect(defaultValues.serialNumber);
                                    } else if (typeof defaultValues.serialNumber === 'number') {
                                        this.openServiceConnect(defaultValues.serialNumber.toString());
                                    } else {
                                        toastr.error(
                                            this.props.t('toastMessage:error'),
                                            'Invalid serial number. Required to open Service Connect.',
                                            constants.toastrError
                                        );
                                    }
                                }}
                                disabled={disabled}
                                aria-label="Open in Service Connect"
                                role="link"
                                tabIndex={disabled ? -1 : 0}
                                aria-disabled={disabled}
                            >
                                Service Connect
                            </Button>
                        )}
                    </Col>
                )
            },
            rfid: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'rfid',
                    colWidth: 12,
                    type: 'input',
                    name: 'rfid',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(20)
                },
                formState: { value: defaultValues.rfid, disabled }
            },
            sapEquipmentNumber: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'sapEquipmentNumber',
                    colWidth: 6,
                    type: 'input',
                    name: 'sapEquipmentNumber',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(20)
                },
                formState: { value: defaultValues.sapEquipmentNumber, disabled: true }
            },
            sapMaterialNumber: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'sapMaterialNumber',
                    colWidth: 6,
                    type: 'input',
                    name: 'sapMaterialNumber',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(20)
                },
                formState: { value: defaultValues.sapMaterialNumber, disabled: true }
            },
            remarks: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'remarks',
                    colWidth: 12,
                    componentClass: 'textarea',
                    name: 'remarks',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(1000)
                },
                formState: { value: defaultValues.remarks, disabled }
            },
            quantity: {
                options: {
                    validators: quantityValidators
                },
                render: FormUtil.TextInput,
                inputType: 'number',
                meta: {
                    label: 'quantity',
                    colWidth: 6,
                    type: 'number',
                    defaultValue: 1,
                    name: 'quantity',
                    style: shouldRequireQuantity ? undefined : { display: 'none' },
                    t: this.props.t
                },
                formState: { value: defaultValues.quantity || 1, disabled: isCapitalEquipmentType }
            },
            $field_2: {
                isStatic: false, // ensures a key is added
                render: () => (
                    <FormUtil.TextLabelStatic
                        meta={{
                            label: this.props.t('locationLabel'),
                            colWidth: 12
                        }}
                    />
                )
            },
            $field_3: {
                isStatic: false, // ensures a key is added
                render: () => (
                    <Col
                        xs={12}
                        style={{
                            display: `${defaultValues.importedLocation ? 'block' : 'none'}`
                        }}
                    >
                        <FormGroup>
                            <FormLabel>{String(this.props.t('imported location'))}</FormLabel>
                            <h5 className="queue-form-label">{String(defaultValues.importedLocation)}</h5>
                        </FormGroup>
                    </Col>
                )
            },
            buildingID: {
                render: FormUtil.CreatableSelect,
                meta: {
                    options: defaultValues.buildingOptions,
                    label: 'building',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    handleCreate: this.handleCreateBuilding,
                    name: 'building',
                    required: shouldRequireLocation,
                    isClearable: true,
                    t: this.props.t
                },
                options: {
                    validators: [this.locationGroupValidator]
                },
                formState: {
                    value: defaultValues.buildingID,
                    disabled
                }
            },
            floorID: {
                render: FormUtil.CreatableSelect,
                meta: {
                    options: defaultValues.floorOptions,
                    label: 'floor',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    handleCreate: this.handleCreateFloor,
                    name: 'floor',
                    required: shouldRequireLocation,
                    isClearable: true,
                    t: this.props.t
                },
                options: {
                    validators: [this.locationGroupValidator]
                },
                formState: {
                    value: defaultValues.floorID,
                    disabled
                }
            },
            locationID: {
                render: FormUtil.CreatableSelect,
                meta: {
                    options: defaultValues.locationOptions,
                    label: 'location',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    handleCreate: this.handleCreateLocation,
                    name: 'location',
                    required: shouldRequireLocation,
                    isClearable: true,
                    t: this.props.t
                },
                options: {
                    validators: [this.locationGroupValidator]
                },
                formState: {
                    value: defaultValues.locationID,
                    disabled
                }
            },
            roomID: {
                render: FormUtil.CreatableSelect,
                meta: {
                    options: defaultValues.roomOptions,
                    label: 'room',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    handleCreate: this.handleCreateRoom,
                    name: 'room',
                    required: false,
                    isClearable: true,
                    t: this.props.t
                },
                formState: {
                    value: defaultValues.roomID,
                    disabled
                }
            },
            position: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'position',
                    colWidth: 12,
                    name: 'position',
                    required: false,
                    t: this.props.t
                },
                options: {
                    validators: Validators.maxLength(145)
                },
                formState: { value: defaultValues.position, disabled }
            },
            system: {
                render: FormUtil.Select,
                meta: {
                    options: constants.constructSystems(this.props.t),
                    label: 'system',
                    colWidth: 12,
                    placeholder: 'nsJob:typePlaceholder',
                    name: 'system',
                    disableSort: true,
                    required: false,
                    isClearable: true,
                    t: this.props.t
                },
                formState: { value: defaultValues.system, disabled }
            },
            prodDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'manufactureDate',
                    colWidth: 12,
                    showTime: false,
                    name: 'manufacture-date',
                    required: false,
                    placeholder: 'DD-MMM-YY',
                    t: this.props.t
                },
                options: {
                    validators: [FormUtil.validators.isValidMoment]
                },
                formState: { value: defaultValues.prodDate, disabled }
            },
            $field_4: {
                isStatic: false, // ensures a key is added
                render: () => (
                    <FormUtil.TextLabelStatic
                        meta={{
                            label: this.props.t('labelOtherDates'),
                            colWidth: 12
                        }}
                    />
                )
            },
            installDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'installDate',
                    colWidth: 12,
                    showTime: false,
                    name: 'install-date',
                    required: false,
                    placeholder: 'DD-MMM-YY',
                    t: this.props.t
                },
                options: {
                    validators: [FormUtil.validators.isValidMoment]
                },
                formState: { value: defaultValues.installDate, disabled }
            }
        } as { [key: string]: GroupProps };

        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            this.props.t
        );
    };

    handleNewManufactureDate = (value: string | null) => {
        if (moment.isMoment(value) === false) {
            return;
        }
        const newDate = moment.isMoment(value) ? value.format(constants.momentSQLFormat) : value;
        let newDateFieldValues: { [key: string]: any } = { prodDate: newDate };
        if (newDate) {
            const dates = ['installDate'];
            dates.forEach(date => {
                const dateValue = this.props.formValues[date];
                if (!dateValue) {
                    newDateFieldValues = {
                        ...newDateFieldValues,
                        [date]: newDate
                    };
                }
            });
            this.props.updateInstallFormValues(newDateFieldValues);
        }
    };

    handleFieldLengthError = (name: string) => {
        if (name.length > 250) {
            toastr.error('Field too long', '', constants.toastrError);
            return true;
        }
    };

    handleCreateBuilding = (name: string) => {
        if (this.handleFieldLengthError(name)) {
            return;
        }
        const locationObject = this.props.saveAnyLocation(name, this.props.facility.id, {}, true);
        this.props.updateInstallFormValues({
            buildingID: { label: name, value: locationObject.id }
        });
    };

    handleCreateFloor = (name: string) => {
        const { buildingID } = this.formGroup.value;
        if (!buildingID) {
            toastr.error('Please select a building first.', '', constants.toastrError);
            return;
        }
        if (this.handleFieldLengthError(name)) {
            return;
        }
        const locationObject = this.props.saveAnyLocation(name, this.props.facility.id, {
            buildingID: buildingID.value
        }, true);
        this.props.updateInstallFormValues({
            floorID: { label: name, value: locationObject.id }
        });
    };

    handleCreateLocation = (name: string) => {
        const { buildingID, floorID } = this.formGroup.value;
        if (!buildingID || !floorID) {
            toastr.error('Please select a building and a floor first.', '', constants.toastrError);
            return;
        }
        if (this.handleFieldLengthError(name)) {
            return;
        }
        const locationObject = this.props.saveAnyLocation(name, this.props.facility.id, {
            buildingID: buildingID.value,
            floorID: floorID.value
        }, true);
        this.props.updateInstallFormValues({
            locationID: { label: name, value: locationObject.id }
        });
    };

    handleCreateRoom = (name: string) => {
        const { buildingID, floorID, locationID } = this.formGroup.value;
        if (!buildingID || !floorID || !locationID) {
            toastr.error('Please select a building, floor, and location first.', '', constants.toastrError);
            return;
        }
        if (this.handleFieldLengthError(name)) {
            return;
        }
        const locationObject = this.props.saveAnyLocation(name, this.props.facility.id, {
            buildingID: buildingID.value,
            floorID: floorID.value,
            locationID: locationID.value
        }, true);
        this.props.updateInstallFormValues({
            roomID: { label: name, value: locationObject.id }
        });
    };

    getAllFilteredLocations = (buildingID?: string, floorID?: string, locationID?: string, roomID?: string) => {
        let selectedBuilding, selectedFloor, selectedLocation, selectedRoom;

        if (buildingID) {
            selectedBuilding = find(this.props.facility.buildings, {
                id: buildingID
            }) as Ibuilding;
            if (selectedBuilding) {
                selectedFloor = find(selectedBuilding.floors, {
                    id: floorID
                });
                if (selectedFloor) {
                    selectedLocation = find((selectedFloor as Ifloor).locations, {
                        id: locationID
                    });
                    if (selectedLocation) {
                        selectedRoom = find((selectedLocation as Ilocation).rooms, {
                            id: roomID
                        });
                    }
                }
            }
        }

        const buildingOptions = FormUtil.convertToOptions(this.props.facility.buildings);
        const floorOptions = FormUtil.convertToOptions(selectedBuilding ? selectedBuilding.floors : []);
        const locationOptions = FormUtil.convertToOptions(selectedFloor ? (selectedFloor as Ifloor).locations : []);
        const roomOptions = FormUtil.convertToOptions(selectedLocation ? (selectedLocation as Ilocation).rooms : []);

        return {
            buildingOptions,
            floorOptions,
            locationOptions,
            roomOptions,
            buildingID: FormUtil.convertToSingleOption(selectedBuilding),
            floorID: FormUtil.convertToSingleOption(selectedFloor),
            locationID: FormUtil.convertToSingleOption(selectedLocation),
            roomID: FormUtil.convertToSingleOption(selectedRoom)
        };
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();

        // cross-field validators only re-run on their own control's change
        locationGroupFields.forEach(key => {
            const control = this.formGroup.get(key);
            if (control) {
                control.updateValueAndValidity({ emitEvent: false });
            }
        });
        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }

        if (this.props.facility) {
            if (this.props.enableBulkInstallBaseMode && this.props.selection) {
                // BULK EDIT
                const toastrConfirmOptions = {
                    onOk: () => {
                        this.props.bulkUpdateInstalls(this.formValuesToItem());
                    },
                    onCancel: () => null,
                    okText: this.props.t('buttonBulkUpdateInstalls'),
                    cancelText: this.props.t('common:cancel')
                };
                toastr.confirm(
                    this.props.t('toastMessage:confirmBulkUpdateInstalls', {
                        count: this.props.selection.length
                    }),
                    toastrConfirmOptions
                );
            } else if (this.props.selectedItem.id.length) {
                // EDIT
                this.props.updateInstall(this.formValuesToItem(), this.props.t, true);
            } else if (this.props.selectedProduct && this.props.selectedProduct.id.length) {
                // SAVE NEW
                this.props.saveInstall(this.formValuesToItem(), this.props.t, this.props.selectedProduct);
            } else {
                console.error('[handleSubmit]: missing product when saving a new install.');
            }
        } else {
            console.error('[handleSubmit]: missing facility, unable to save install');
            toastr.error('Error', 'Missing facility, please try again or contact support', constants.toastrError);
        }
    };

    handleDelete = () => {
        const bulkMode = this.props.enableBulkInstallBaseMode && this.props.selection;

        // Bulk delete routes through the API BulkDelete endpoint, which rejects
        // batches over constants.bulkDeleteSelectionLimit. Block oversized selections
        // here so they fail fast in the UI rather than being rejected server-side.
        if (bulkMode && (this.props.selection?.length || 0) > constants.bulkDeleteSelectionLimit) {
            toastr.warning(
                this.props.t('toastMessage:warning'),
                this.props.t('toastMessage:bulkSelectionLimit', { limit: constants.bulkDeleteSelectionLimit }),
                constants.toastrWarning
            );
            return;
        }

        // They want to display the SAP asset delete message for all assets, keeping this in here for now in
        // case they change their mind because this message makes no sense to display for all assets
        const needsDoubleCheck = true; // this.props.selectedItem.workOrders?.find(wo => wo.source === WorkOrderSource.SAP);
        const del = bulkMode
            ? () => {
                  this.props.bulkUpdateInstalls({ isDeleted: true });
              }
            : () => {
                  if (this.props.match.url !== '/devices') {
                      this.props.history.push('/devices');
                  }
                  this.props.deleteInstall(this.props.selectedItem.id);
              };

        const toastrConfirmAgainOptions = {
            onOk: del,
            onCancel: () => null,
            okText: this.props.t('installDeleteOk'),
            cancelText: this.props.t('common:cancel')
        };

        const okText = bulkMode ? this.props.t('buttonBulkDeleteInstalls') : this.props.t('installDeleteOk');
        const toastrText = bulkMode
            ? this.props.t('toastMessage:confirmBulkDeleteInstalls', {
                  count: this.props.selection?.length || 0
              })
            : this.props.t('installDeleteConfirm');

        const toastrConfirmOptions = {
            onOk: needsDoubleCheck
                ? () => {
                      toastr.confirm(this.props.t('installDeleteSecondWarning'), toastrConfirmAgainOptions);
                  }
                : del,
            onCancel: () => null,
            okText,
            cancelText: this.props.t('common:cancel')
        };
        toastr.confirm(toastrText, toastrConfirmOptions);
    };

    setForm = (form: AbstractControl) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };
        if (!this.subscription) {
            setTimeout(() => {
                this.subscribeToChanges();
            }, 300);
        }
    };

    canEditInstalls = () => {
        return (
            this.props.isJobReadOnly === false &&
            (constants.hasSecurityFunction(this.props.user, constants.securityFunctions.ManageInventory.id) ||
                constants.hasSecurityFunction(this.props.user, constants.securityFunctions.FSE.id))
        );
    };

    canDeleteSAPAsset = () => {
        return constants.hasSecurityFunction(this.props.user, constants.securityFunctions.DeleteSAPAsset.id);
    };

    generateSerialNumber = () => {
        if (!this.state.isGeneratingSerialNumber) {
            this.setState({ isGeneratingSerialNumber: true });
            msalFetch(API.inventory.getNextSerialNumber, { method: 'GET' })
                .then(result => {
                    const serialNumber = result.data;
                    this.formGroup.patchValue({ serialNumber });
                })
                .catch(error => constants.handleError(error, 'generate serial number'))
                .finally(() => {
                    this.setState({ isGeneratingSerialNumber: false });
                });
        }
    };

    render() {
        const { t } = this.props;
        const formClassName = `mb-4 ${this.props.colorButton}`;
        const deleteButtonStyle =
            !this.props.selectedItem.id.length && this.props.enableBulkInstallBaseMode === false
                ? { marginRight: '15px', display: 'none' }
                : { marginRight: '15px' };

        return (
            <div className={formClassName}>
                <form onSubmit={this.handleSubmit} className="clearfix beacon-form">
                    <Col xs={12}>
                        <Row>
                            <FormGenerator onMount={this.setForm} fieldConfig={this.state.fieldConfig} />
                        </Row>
                    </Col>
                    <Col xs={12} className="form-buttons text-right">
                        {this.props.showCancel !== false && (
                            <Button
                                variant="default"
                                type="button"
                                className="cancel-button"
                                onClick={this.props.toggleModal}
                            >
                                {String(t('common:cancel'))}
                            </Button>
                        )}
                        {this.props.isJobReadOnly === false && (
                            <>
                                <Button
                                    variant={this.props.formValues.sapEquipmentNumber ? 'grey' : 'warning'}
                                    style={deleteButtonStyle}
                                    type="button"
                                    disabled={
                                        this.props.loading ||
                                        !!this.props.formValues.sapEquipmentNumber ||
                                        !this.canDeleteSAPAsset()
                                    }
                                    onClick={this.handleDelete}
                                >
                                    {String(t('common:delete'))}
                                </Button>
                                <Button variant={this.props.colorButton} type="submit" disabled={this.props.loading}>
                                    {String(t('common:save'))}
                                </Button>
                            </>
                        )}
                    </Col>
                </form>
            </div>
        );
    }
}
export default ManageInstallForm;
